import { makeStyles } from '@material-ui/styles'
import { RootState, useAppSelector } from 'app/store'
import StepperModalForm, { ModalStep } from 'core/elements/modal/StepperModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { createKaapiClusterAddonBody } from 'k8s/components/kaapi/cluster-addons/action-helpers'
import { createKaapiClusterAddon } from 'k8s/components/kaapi/cluster-addons/actions'
import { createKaapiCluster } from 'k8s/components/kaapi/clusters/actions'
import { kaapiClustersSelector } from 'k8s/components/kaapi/clusters/selectors'
import { listKaapiConfigMaps } from 'k8s/components/kaapi/config-maps/actions'
import { kaapiConfigMapsSelector } from 'k8s/components/kaapi/config-maps/selectors'
import { addClusterLabelToByoHost, listByoHosts } from 'k8s/components/nodes/byoh/actions'
import ValidatedForm from 'pf9-ui-components/built/components/validatedForm/ValidatedForm'
import Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import { prop } from 'ramda'
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import ClusterArchitectureFieldsBYO from './form-components/ClusterArchitectureFieldsBYO'
import ClusterConfigFields from './form-components/ClusterConfigFields'
import ClusterNameField from './form-components/ClusterNameField'
import { getDUInternalIP } from './form-components/helpers'
import { isAirgapped } from 'core/utils/helpers'
import { ProgressStatus, StepProgress } from 'core/components/progress/StepProgress'
import { generateApiErrorPayload } from 'api-client/helpers'
import {
  ProgressStep,
  useClusterProgress,
} from 'k8s/components/access-control/hooks/useClusterProgress'
import { createKaapiClusterClassBody } from 'k8s/components/kaapi/clusters/action-helpers'
import { METALLB_VERSION, waitForTopologyReconciliation } from './helper'

export const UNSUPPORTED_VM_FLAVORS = ['pf9.unknown', 'm1.tiny', 'm1.small']

const initialValues = {
  physicalNodes: [],
  calico: true,
  coreDns: true,
  enableAddons: true,
  controlPlaneType: 'managed',
  apiServerFlags: '',
  schedulerFlags: '',
  controllerManagerFlags: '',
  metallb: false,
}

/**
 * @summary - This component is used to deploy a k8s workload cluster with Physical nodes (BYOH)
 * @flow
 * 1. Cluster Infrastructure:
 *    - User selects BYO from the radio group
 * 2. Cluster Architecture:
 *    - PhysicalNodesTable will be rendered - User can add nodes to the cluster
 *    - User selects the control plane type and its configuration
 * 3. Cluster Configuration: (Same as virtualized cluster)
 * 4. Final Touches -
 *    - Cluster Name: User enters the cluster name
 * 5. User clicks the "Add Cluster" button - handleBYOClusterCreation will be called
 *    - Validate the fields - physical nodes should be selected
 *    - We have added error handling after each API call
 *    - Create Cluster object
 *    For Each Physical Node
 *      - Update the host labels to include cluster name
 *    - Create HostedControlPlane object
 *    - Create BYOCluster object
 *    - Create KubeadmConfigTemplate object
 *    - Create BYOMachineTemplate object
 *    - Create MachineDeployment object
 *    - Create ClusterAddons object
 *    - Create K8sInstaller object
 * 6.On success, the user will be redirected to the cluster detail page
 */
const ClusterDeploymentForm = ({ onClose }) => {
  const classes = useStyles()
  const { params, getParamsUpdater } = useParams<Record<string, any>>(initialValues)
  const { updatePrefs: updateK8sPluginGlobalParams } = useScopedPreferences('k8sPluginGlobalParams')
  const { activeKaapiTenant, activeRegion, domain, activeTenant } = useSelector<
    RootState,
    SessionState
  >(prop(sessionStoreKey))
  const namespace = activeKaapiTenant
  const [physicalNodeErrors, setPhysicalNodeErrors] = useState<{
    title?: string
    message?: string
  }>(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [showProgress, setShowProgress] = useState(false)
  const [isProgressComplete, setIsProgressComplete] = useState(false)
  const {
    progressDetails: clusterClassProgressDetails,
    updateProgress,
    resetProgress,
  } = useClusterProgress({
    initLabel: 'Setting Up Cluster',
  })

  const isAirgappedEnv = isAirgapped()

  // Load Clusters
  // const { loading: loadingClusters } = useListAction(listKaapiClusters)
  const clusters = useAppSelector(kaapiClustersSelector)

  // Physical Nodes
  const { reload: reloadPhysicalNodes } = useListAction(listByoHosts)

  // Load Config Maps
  const { loading: loadingConfigMaps } = useListAction(listKaapiConfigMaps, {
    params: {
      namespace,
    },
    requiredParams: ['namespace'],
  })
  const configMaps = useSelector(kaapiConfigMapsSelector)
  const duInternalIP = getDUInternalIP(configMaps)

  // Create Actions
  const {
    update: createCluster,
    updating: creatingCluster,
    error: createClusterError,
  } = useUpdateAction(createKaapiCluster)
  const {
    update: createClusterAddon,
    updating: creatingClusterAddon,
    error: createClusterAddonError,
  } = useUpdateAction(createKaapiClusterAddon)

  // BYOCluster Actions
  const {
    update: addClusterLabelToByoHostFn,
    updating: addingClusterLabelToByoHost,
    error: addClusterLabelToByoHostError,
  } = useUpdateAction(addClusterLabelToByoHost)

  const hasError =
    !!createClusterError || !!createClusterAddonError || !!addClusterLabelToByoHostError

  useEffect(() => {
    if (!hasError) return
    // Show first error
    const error = createClusterError || createClusterAddonError || addClusterLabelToByoHostError

    setError({
      title: error?.title || 'Error creating cluster',
      message: error?.message || 'An error occurred while creating the cluster. Please try again.',
    })
  }, [hasError, createClusterError, createClusterAddonError, addClusterLabelToByoHostError])

  const handleError = useCallback(
    (error: any, message: string, step?: ProgressStep) => {
      setLoading(false)
      setError(error || { title: 'Error', message })
      if (step) {
        updateProgress(step, ProgressStatus.Failed)
      }
    },
    [updateProgress],
  )

  // Handle BYO cluster creation
  const handleBYOClusterCreation = async () => {
    setError(null)
    setShowProgress(true)
    setLoading(true)
    setPhysicalNodeErrors(null)

    // Check if physical nodes are provided
    if (params?.physicalNodes?.length === 0) {
      setPhysicalNodeErrors({ title: 'Error', message: 'Please select atleast one physical node' })
      updateProgress(ProgressStep.Init, ProgressStatus.Failed)
      setLoading(false)
      setShowProgress(false)
      return
    }

    updateK8sPluginGlobalParams({ showProgressStep: clusters?.length === 0 })
    try {
      // Create Cluster
      const clusterCreateBody = createKaapiClusterClassBody({
        name: params.clusterName,
        namespace,
        kubernetesVersion: params.k8sVersion,
        isBYOCluster: true,
        gpuAddons: params.gpuAddons,
        region: activeRegion,
        domain: domain?.id,
        tenant: activeTenant,
        replicas: params.physicalNodes.length,
        apiServerFlags: params.apiServerFlags ? params.apiServerFlags.split(',') : [],
        schedulerFlags: params.schedulerFlags ? params.schedulerFlags.split(',') : [],
        controllerManagerFlags: params.controllerManagerFlags
          ? params.controllerManagerFlags.split(',')
          : [],
      })
      const { success: createClusterSuccess, error: createClusterError } = await createCluster({
        namespace,
        body: clusterCreateBody,
      })
      // If cluster creation fails, return
      if (!createClusterSuccess) {
        handleError(createClusterError, 'Failed to create cluster', ProgressStep.Init)
        return
      }
      updateProgress(ProgressStep.Init, ProgressStatus.Success)

      // Update the selected nodes
      const res = await Promise.all(
        params.physicalNodes.map((node) =>
          addClusterLabelToByoHostFn({ name: node?.name, clusterName: params.clusterName }),
        ),
      )

      // If any of the promises failed, return
      if (res?.some((item) => !item?.success)) {
        handleError(null, 'Failed to update labels on physical nodes', ProgressStep.ControlPlane)
        return
      }

      // Create ClusterAddon
      if (params.metallb) {
        const metallbBody = createKaapiClusterAddonBody({
          clusterName: params.clusterName,
          namespace,
          addonType: 'metallb',
          version: METALLB_VERSION,
        })
        const {
          success: createClusterAddonSuccess,
          error: createClusterAddonError,
        } = await createClusterAddon({ namespace, body: metallbBody })
        if (!createClusterAddonSuccess) {
          handleError(
            createClusterAddonError,
            'Failed to create ClusterAddon',
            ProgressStep.ControlPlane,
          )
          return
        }
      }

      updateProgress(ProgressStep.ControlPlane, ProgressStatus.Success)

      if (!hasError) {
        try {
          await waitForTopologyReconciliation({ clusterName: params.clusterName })
          updateProgress(ProgressStep.NodeGroups, ProgressStatus.Success)
          setIsProgressComplete(true)
          reloadPhysicalNodes()
          updateK8sPluginGlobalParams({ cluster: params.clusterName })
        } catch (err) {
          handleError(err, 'Failed to redirect to cluster list page', ProgressStep.NodeGroups)
          return
        }
      }
    } finally {
      setLoading(false)
    }
  }

  // Reset form callback
  const handleReset = useCallback(() => {
    setError(null)
    setLoading(false)
    setIsProgressComplete(false)
    setShowProgress(false)
    updateK8sPluginGlobalParams({ showProgressStep: false })
    resetProgress()
    onClose()
  }, [resetProgress, onClose])

  const submitting =
    creatingCluster || creatingClusterAddon || addingClusterLabelToByoHost || loading

  const steps: ModalStep[] = [
    {
      stepId: 1,
      headerTitle: 'Cluster Architecture',
      description:
        'Select the compute infrastructure for this cluster. Using Physical Servers provides direct access to bare metal resources, offering maximum performance and isolation for specialized or high-throughput workloads.',
      component: (
        <div className={classes.formSection}>
          <ClusterNameField
            params={params}
            getParamsUpdater={getParamsUpdater}
            clusters={clusters}
          />
          <ClusterArchitectureFieldsBYO
            params={params}
            getParamsUpdater={getParamsUpdater}
            physicalNodeErrors={physicalNodeErrors}
            setError={setError}
          />
        </div>
      ),
      stepValidator: () => {
        if (isAirgappedEnv && !duInternalIP) {
          setError({
            title: 'Error',
            message:
              'Something went wrong, please try again. Tenant may not be configured correctly',
          })
          return {
            success: false,
          }
        }
        if (!params?.physicalNodes?.length) {
          setPhysicalNodeErrors({
            title: 'Error',
            message: 'Please select atleast one physical node',
          })
          return {
            success: false,
          }
        }
        return { success: true }
      },
    },
    {
      stepId: 2,
      headerTitle: 'Configure your Cluster',
      description:
        'Configure your cluster’s Kubernetes version, your GPUs partitioning strategy (if applicable), optional Kubernetes flags, and add-ons.',
      component: (
        <div className={classes.formSection}>
          <ClusterConfigFields params={params} getParamsUpdater={getParamsUpdater} isBYOCluster />
        </div>
      ),
    },
  ]

  return (
    <StepperModalForm
      steps={steps}
      title="Deploy A New Cluster"
      maxWidth={876}
      open={true}
      onClose={handleReset}
      onSubmit={showProgress || isProgressComplete ? handleReset : handleBYOClusterCreation}
      error={error}
      resetError={() => setError(null)}
      submitting={submitting}
      ValidatedFormComponent={ValidatedForm as any}
      params={params}
      isProgressComplete={isProgressComplete}
      showProgress={showProgress}
      resetProgress={resetProgress}
      customStepProgressComponent={
        <StepProgress
          progressDetails={clusterClassProgressDetails}
          title={`Cluster deployment is in progress. 
                    Please wait and do not close this dialog, or the deployment will be cancelled.`}
          errorMessage={error?.message || generateApiErrorPayload(error)}
        />
      }
    />
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  formSection: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridGap: theme.spacing(2),
  },
}))

export default ClusterDeploymentForm
